import { useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useFirstRun } from '../../contexts/FirstRunContext';
import FirstRunHeader from './FirstRunHeader';
import { firstRunPosition } from './firstRun';

/**
 * "Skip setup": leave first run for Home and forget the revenue. Before the
 * revenue step finishes this records the skip; after it, onboarding is
 * already complete, so it only leaves.
 */
export function useSkipSetup(): () => void {
  const { isOnboardingComplete, skipOnboarding } = useOnboarding();
  const { setRevenue } = useFirstRun();
  const navigate = useNavigate();
  return useCallback(() => {
    setRevenue(null);
    // skipOnboarding flips the local status before its request, so Home
    // renders straight away instead of redirecting back to /welcome.
    if (!isOnboardingComplete) void skipOnboarding();
    // Replace, so the browser's back button doesn't land back in setup.
    navigate('/', { replace: true });
  }, [isOnboardingComplete, skipOnboarding, setRevenue, navigate]);
}

/**
 * Layout route around every page first run can visit. It stays mounted as
 * the seller moves between them, so the header (and its bar) carries over
 * from one step to the next.
 */
export default function FirstRunFrame() {
  const location = useLocation();
  const skip = useSkipSetup();
  const position = firstRunPosition(location.pathname, location.state);

  return (
    <>
      {position && (
        <FirstRunHeader step={position.step} progress={position.progress} onSkip={skip} />
      )}
      <Outlet />
    </>
  );
}
